import _Array$from from "@babel/runtime-corejs3/core-js/array/from";
import _forEachInstanceProperty from "@babel/runtime-corejs3/core-js/instance/for-each";
import _globalThis from "@babel/runtime-corejs3/core-js/global-this";
import _setTimeout from "@babel/runtime-corejs3/core-js/set-timeout";
import React from "react";
import PropTypes from "prop-types";
import matches from "matches-selector";
import analyticsWrapper from "@xo-union/tk-analytics";
import metaKeyPressed from "@segment/is-meta";
import noop from "lodash/noop";
import getMappableValue from "../getMappableValue.js";
export const eventWillChangePage = event => {
  const element = event.target;
  const MOUSE_MIDDLE_BUTTON = 1;
  const willOpenNewTab = metaKeyPressed(event) || event.button === MOUSE_MIDDLE_BUTTON;
  const target = element.getAttribute('target');
  const href = element.getAttribute('href');
  return element.tagName === 'A' && href && target !== '_blank' && !willOpenNewTab;
};
const getContextEventData = _ref => {
  let {
    element,
    contextEnd
  } = _ref;
  const data = {};
  const source = {};
  let currentElement = element;
  const processAttribute = attribute => {
    const match = attribute.name.match(/^data-trackable-context-(.+)$/);
    if (match) {
      const contextAttributeName = match[1];
      if (data[contextAttributeName]) {
        /* eslint-disable no-console */
        console.warn(`Trackable context property \`${contextAttributeName}\` is overwritten\n`, 'Previously defined by: ', currentElement, '\n', 'Overwritten by: ', source[contextAttributeName]);
      } else {
        data[contextAttributeName] = attribute.value;
        source[contextAttributeName] = currentElement;
      }
    }
  };
  while (currentElement && currentElement !== contextEnd) {
    const attributes = _Array$from(currentElement.attributes);
    _forEachInstanceProperty(attributes).call(attributes, processAttribute);
    currentElement = currentElement.parentElement;
  }
  return data;
};
export const createLinkFollowStrategy = function () {
  let {
    timeout = 300,
    location = _globalThis.location
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return event => {
    const element = event.target;
    if (eventWillChangePage(event)) {
      const href = element.getAttribute('href');
      event.preventDefault();
      _setTimeout(() => {
        // eslint-disable-next-line no-param-reassign
        location.href = href;
      }, timeout);
    }
  };
};
export const useGenericClickTracker = _ref2 => {
  let {
    eventName,
    eventData,
    trackableSelector = 'a',
    analytics: analyticsProp,
    followStrategy: followStrategyProp
  } = _ref2;
  const defaultFollowStrategy = React.useCallback(createLinkFollowStrategy(), []);
  const followStrategy = followStrategyProp === undefined ? defaultFollowStrategy : followStrategyProp;
  return React.useCallback(event => {
    const element = event.target;
    const analytics = analyticsProp || analyticsWrapper;
    if (matches(element, trackableSelector)) {
      const contextProperties = getContextEventData({
        element,
        contextEnd: event.currentTarget
      });
      const finalEventName = getMappableValue(eventName, element, contextProperties);
      const finalEventData = getMappableValue(eventData, element, contextProperties);
      analytics.track(finalEventName, finalEventData);
      if (followStrategy === false) {
        event.preventDefault();
      }
      if (typeof followStrategy === 'function') {
        followStrategy(event);
      }
    }
  }, [eventName, eventData, analyticsProp, followStrategy, trackableSelector]);
};
const GenericClickTracker = _ref3 => {
  let {
    as: ComponentToRender,
    children,
    onClick,
    ...props
  } = _ref3;
  const handleAnalytics = useGenericClickTracker(props);
  const finalOnClick = React.useCallback(function () {
    handleAnalytics(...arguments);
    return onClick(...arguments);
  }, [onClick, handleAnalytics]);
  return /*#__PURE__*/React.createElement(ComponentToRender, {
    onClick: finalOnClick
  }, children);
};
process.env.NODE_ENV !== "production" ? GenericClickTracker.propTypes = {
  /**
   * Component or element to render
   */
  as: PropTypes.elementType,
  /**
   * The trackable components
   */
  children: PropTypes.node.isRequired,
  /**
   * Selector of elements that are trackable
   */
  trackableSelector: PropTypes.string,
  /**
   * Event name to report in analytics track call
   */
  eventName: PropTypes.oneOfType([PropTypes.func, PropTypes.string]).isRequired,
  /**
   * Object with event data to report in analytics track call
   */
  eventData: PropTypes.oneOfType([PropTypes.func, PropTypes.shape()]).isRequired,
  /**
   * analytics library dependency injection
   */
  analytics: PropTypes.shape({
    track: PropTypes.func
  }),
  /**
   * This is a function implementing the strategy to follow a link after it has been clicked.
   * `false` disables opening of the link.
   * This only applies to trackable links that will leave the page. If a link has a `target="_blank"` property
   * or the user is attempting to open a new tab, the follow strategy will have no effect.
   */
  followStrategy: PropTypes.oneOfType([PropTypes.oneOf([false, null]), PropTypes.func]),
  /**
   * Extend onClick behavior
   */
  onClick: PropTypes.func
} : void 0;
GenericClickTracker.defaultProps = {
  followStrategy: createLinkFollowStrategy(),
  trackableSelector: 'a',
  as: 'div',
  onClick: noop
};
export default GenericClickTracker;